import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EntityFormComponent } from 'app/components/entity-form/entity-form.component';
import { UserService } from 'app/rest/user.service';
import { Rest } from 'app/rest/rest_client';
import { NotificationsService } from 'app/services/notifications-service/notifications.service';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { I18nService } from 'app/services/i18n/i18n.service';
import { SanctionService } from 'app/modules/vehicle-inventory/services/table-management/sanction.service';
import { SanctionReasonService } from 'app/modules/vehicle-inventory/services/table-management/sanctionReason.service';
import { InvVehicleService } from 'app/modules/vehicle-inventory/services/invVehicle.service';
import { DriverService } from 'app/modules/vehicle-inventory/services/driver.service';
import { LeaderService } from 'app/rest/leader.service';
import { UserTimePipe } from 'app/components/shared/UserTimePipe';

@Component({
  selector: 'app-sanction-form',
  templateUrl: './sanction-form.component.html',
  styleUrls: ['./sanction-form.component.css'],
  providers: [SanctionService, InvVehicleService, DriverService, LeaderService, SanctionReasonService],
  standalone: false,
})
export class SanctionFormComponent extends EntityFormComponent<Rest.InvSanction> implements OnInit {

  @Input() vehicle?: Rest.Vehicle = null;
  @Input() driver?: Rest.InvDriver = null;

  title: string;

  ids: number[] = [];
  idsObservations: number[] = [];

  disableSave: boolean;
  formManipulation: boolean;

  sanctionDate: Date;
  editVehicle: boolean;

  vehicleList: Rest.Vehicle[] = [];
  driverList: Rest.InvDriver[] = [];
  responsibleList: Rest.User[] = [];
  sanctionReasonList: Rest.SanctionReason[] = [];

  vehicleRequestContext: any;
  driverRequestContext: any;
  leaderRequestContext: any;
  sanctionReasonRequestContext: any;

  errorMessages: string[] = [];

  constructor(
    private sanctionService: SanctionService,
    notificationsService: NotificationsService,
    protected i18n: I18nService,
    private authenticationService: AuthenticationService,
    public translateService: TranslateService,
    public invVehicleService: InvVehicleService,
    public driverService: DriverService,
    public leaderService: LeaderService,
    public sanctionReasonService: SanctionReasonService,
    private userTime: UserTimePipe
  ) {
    super(sanctionService, notificationsService, i18n, authenticationService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setPermissions();
  }

  beforeShow() {
    this.errorMessages = [];
    this.ids = [];
    this.idsObservations = [];

    if (this.entity.documents && this.entity.documents.length > 0) {
      this.entity.documents.forEach((x) => this.ids.push(x.id));
    } else {
      this.ids = [-1];
    }

    if (this.entity.observations && this.entity.observations.length > 0) {
      this.entity.observations.forEach((x) => this.idsObservations.push(x.id));
    } else {
      this.idsObservations = [-1];
    }

    this.editVehicle = true;

    if (this.isNew) {
      this.disableSave = false;
      this.title = this.translateService.instant('tableManagement.sanction.createSanction');
      if (this.vehicle) {
        this.entity.vehicle = this.vehicle;
        this.editVehicle = false;
      }
      if (this.driver) {
        this.entity.driver = this.driver;
        // Set vehicle from active driver assignment
        if (!this.entity.vehicle) {
          const activeAssignment = this.getActiveDriverAssignment(this.driver);
          if (activeAssignment) {
            this.entity.vehicle = activeAssignment.vehicle;
          }
        }
      }
    } else {
      this.title = this.translateService.instant('tableManagement.sanction.updateSanction');
      this.disableSave = this.entity['_$update'] !== true;
      if (this.vehicle) {
        this.editVehicle = false;
        this.entity.vehicle = this.vehicle;
      }
    }

    this.sanctionDate = this.entity.date ? this.userTime.utcToCalendarDate(this.entity.date) : null;

    this.vehicleRequestContext = {
      sortBy: 'name', sortAsc: true, pageSize: 10, filters: {}, loadPermissions: false,
    } as Rest.ListPaginationRequest;

    this.driverRequestContext = {
      sortBy: 'employee.code', sortAsc: true, pageSize: 10, filters: {}, loadPermissions: false,
    } as Rest.ListPaginationRequest;

    this.leaderRequestContext = {
      sortBy: 'user.surname1', sortAsc: true, pageSize: 10, filters: {}, loadPermissions: false,
    } as Rest.ListPaginationRequest;
    this.leaderRequestContext.filters['fleetLeader'] = [true];

    this.sanctionReasonRequestContext = {
      sortBy: 'reason', sortAsc: true, pageSize: 100, filters: {}, loadPermissions: false,
    } as Rest.ListPaginationRequest;

    if (this.entity.vehicle) {
      this.vehicleRequestContext.filters['name'] = [this.entity.vehicle.name];
    }
    if (this.entity.driver) {
      this.driverRequestContext.filters['user'] = [
        `${this.entity.driver.employee.code} ${this.entity.driver.employee.user.surname1} ${this.entity.driver.employee.user.surname2}, ${this.entity.driver.employee.user.name}`
      ];
    }
    if (this.entity.responsible) {
      this.leaderRequestContext.filters['user'] = [
        `${this.entity.responsible.surname1} ${this.entity.responsible.surname2}, ${this.entity.responsible.name}`
      ];
    }

    this.invVehicleService.getPage(this.vehicleRequestContext).then((p) => {
      this.vehicleList = p.entities;
    });

    this.driverService.getPage(this.driverRequestContext).then((p) => {
      this.driverList = p.entities.map((d) => ({
        ...d,
        formatDriverLabel: `(${d.employee.code}) ${d.employee.user.surname1} ${d.employee.user.surname2}, ${d.employee.user.name}`,
      }));
    });

    this.leaderService.getPage(this.leaderRequestContext).then((p) => {
      this.responsibleList = p.entities.map((l) => ({
        ...l.user,
        formatResponsibleLabel: `${l.user.surname1} ${l.user.surname2}, ${l.user.name}`,
      }));
    });

    this.sanctionReasonService.getPage(this.sanctionReasonRequestContext).then((p) => {
      this.sanctionReasonList = p.entities;
    });
  }

  afterShow() {}

  validateSanctionForm(): boolean {
    this.errorMessages = [];
    if (this.vehicle) this.entity.vehicle = this.vehicle;
    if (!this.entity.vehicle || !this.entity.driver || !this.entity.reason || !this.entity.issuer || !this.sanctionDate) {
      this.errorMessages.push(this.translateService.instant('error.required-field.generic'));
      window.scroll({ top: 0, behavior: 'smooth' });
    }
    return this.errorMessages.length === 0;
  }

  saveChanges() {
    this.beforeSave();
    if (this.validateSanctionForm()) {
      if (this.isNew) {
        this.sanctionService.create(this.entity).then((p) => {
          this.entity = p;
          this.isNew = false;
          this.sanctionService.refreshList.next(null);
          this.return.emit(p);
        });
      } else {
        this.sanctionService.update(this.entity).then((p) => {
          this.display = false;
          this.sanctionService.refreshList.next(null);
          this.return.emit(p);
        });
      }
    }
  }

  protected beforeSave(): void {
    this.entity.date = this.sanctionDate != null ? new Date(this.userTime.toUTCISOString(this.sanctionDate)) : null;
  }

  refresh() {
    this.return.emit();
  }

  onChangeVehicle(event) {
    this.entity.vehicle = event.value;
    if (event.value?.driver) {
      this.entity.driver = event.value.driver;
    }
  }

  onFilterVehicle(event) {
    this.vehicleRequestContext.filters['name'] = [event.filter ?? ''];
    this.invVehicleService.getPage(this.vehicleRequestContext).then((p) => { this.vehicleList = p.entities; });
  }

  onChangeDriver(event) { this.entity.driver = event.value; }

  onFilterDriver(event) {
    this.driverRequestContext.filters['user'] = [event.filter ?? ''];
    this.driverService.getPage(this.driverRequestContext).then((p) => {
      this.driverList = p.entities.map((d) => ({
        ...d,
        formatDriverLabel: `(${d.employee.code}) ${d.employee.user.surname1} ${d.employee.user.surname2}, ${d.employee.user.name}`,
      }));
    });
  }

  onChangeResponsible(event) { this.entity.responsible = event.value; }

  onFilterResponsible(event) {
    this.leaderRequestContext.filters['user'] = [event.filter ?? ''];
    this.leaderService.getPage(this.leaderRequestContext).then((p) => {
      this.responsibleList = p.entities.map((l) => ({
        ...l.user,
        formatResponsibleLabel: `${l.user.surname1} ${l.user.surname2}, ${l.user.name}`,
      }));
    });
  }

  onChangeReason(event) { this.entity.reason = event.value; }

  onFilterReason(event) {
    this.sanctionReasonRequestContext.filters['reason'] = [event.filter ?? ''];
    this.sanctionReasonService.getPage(this.sanctionReasonRequestContext).then((p) => {
      this.sanctionReasonList = p.entities;
    });
  }

  private getActiveDriverAssignment(driver: Rest.InvDriver): Rest.InvDriverAssignment | null {
    if (!driver.driverAssignments || driver.driverAssignments.length === 0) return null;
    return [...driver.driverAssignments]
      .sort((a, b) => {
        const startDiff = new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
        if (startDiff !== 0) return startDiff;
        const aEnd = a.endDate ? new Date(a.endDate).getTime() : Number.MAX_SAFE_INTEGER;
        const bEnd = b.endDate ? new Date(b.endDate).getTime() : Number.MAX_SAFE_INTEGER;
        return bEnd - aEnd;
      })[0] ?? null;
  }

  setPermissions() {
    const user = this.authenticationService.user;
    this.formManipulation = this.authenticationService.isOneRoleGranted(['INV_SANCTION_CREATE', 'INV_SANCTION_UPDATE'], user);
  }
}

